import { TelegramBotModeEnum } from '@zaoblako/shared/modules/integration/enums/telegram-bot-mode.enum';
import { ProxyServerFindDto, type ProxyServerFindInterface } from '@zaoblako/shared/modules/system/dtos/proxy-server/proxy-server-find.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { IntergrationDtoKeyEnum } from '@zaoblako/shared/modules/integration/enums/intergration-dto-key.enum';
import { SaasDtoKeysEnum } from '@zaoblako/shared/modules/saas/enums/saas-dto-keys.enum';
import { TelegramChatDto, type TelegramChatInterface } from '@zaoblako/shared/modules/integration/dtos/telegram-chat.dto';
import type { FrmInstanceFindInterface } from '@zaoblako/shared/modules/saas/dtos/frm-instance/frm-instance-find.dto';

import * as yup from 'yup';

/** Telegram bot response body - DTO signature */
export interface TelegramBotFindInterface {
  /** Unique identifier (UUID). Cannot be changed. */
  id: string;

  // Tech columns

  /** Bot creation date and time */
  created: Date;
  /** Bot last update date and time */
  updated: Date;
  /** Bot soft delete date and time */
  deleted?: Date;

  // Data columns
  /** Bot auth token */
  token: string;
  /** Bot name */
  name: string;
  /** If `true` then this bot will be used as default bot */
  isDefault?: boolean;
  /** Telegram bot listening mode */
  mode?: TelegramBotModeEnum;
  /** Bot base URL */
  baseUrl: string;
  /** FRM instances using this bot */
  frmInstances?: FrmInstanceFindInterface[];
  /** Active Telegram chats */
  telegramChats?: TelegramChatInterface[];
  /** Proxy server to use */
  proxyServer?: ProxyServerFindInterface;
}

/** Telegram bot response body - DTO schema */
export const TelegramBotFindDto = SerdeDtoClass.create<TelegramBotFindInterface>(IntergrationDtoKeyEnum.TELEGRAM_BOT_FIND_DTO, {
  id: {
    label: 'Номер',
    serializer: yup.string(),
  },
  created: {
    sortable: true,
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    label: 'Изменен',
    serializer: yup.date(),
  },
  deleted: {
    optional: true,
    label: 'Удален',
    serializer: yup.date(),
  },
  token: {
    label: 'Токен авторизации',
    serializer: yup.string(),
  },
  name: {
    sortable: true,
    label: 'Имя бота в Telegram',
    serializer: yup.string(),
  },
  isDefault: {
    optional: true,
    label: 'По-умолчанию',
    hint: 'Если данный флаг включен, то этот бот будет использоваться по-умолчанию, если не задан другой бот',
    serializer: yup.boolean(),
  },
  mode: {
    optional: true,
    label: 'Режим работы',
    hint: 'Определяет, как бот будет получать обновления от Telegram',
    serializer: yup
      .string()
      .oneOf(Object.values(TelegramBotModeEnum)),
  },
  baseUrl: {
    label: 'Ссылка на бота',
    hint: 'Базовая ссылка без параметров',
    serializer: yup.string(),
  },
  frmInstances: {
    optional: true,
    label: 'Инстансы ФРМ, использующие этого бота',
    nested: SaasDtoKeysEnum.FRM_INSTANCE_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
  telegramChats: {
    optional: true,
    nested: TelegramChatDto,
    label: 'Активные чаты в Telegram',
    serializer: (_, nested) => yup.array(nested),
  },
  proxyServer: {
    optional: true,
    label: 'Прокси-сервер',
    nested: ProxyServerFindDto,
    serializer: (_, nested) => nested,
  },
});
